import { Fragment, StrictMode } from 'react'
import type { VFC } from 'react'
import { Size, Spacing } from '@opallabs/gem'
import {
  ConnectedCurrentUserAvatar,
  Conditional,
  openDrawer,
} from '@opallabs/kuiper'
import { capitalCase, ChildrenWithInjectedProps } from '@opallabs/lambda'
import {
  ActionsMenu,
  Button,
  ButtonIconPosition,
  Icon,
  SearchInput,
  DropdownMenu,
} from '@opallabs/lapidary'
import * as styles from './styles'
import type { ViewHeaderProps } from './types'

export { Control as ViewHeaderControl } from './Control'
export type { ControlProps as ViewHeaderControlProps } from './Control'
export { Navigator as ViewHeaderNavigator } from './Navigator'
export type { NavigatorProps as ViewHeaderNavigatorProps } from './Navigator'
export type { ActionMenuOption as ViewHeaderActionMenuOption } from './types'

export type { ViewHeaderProps }

/**
 * Universal view header implementation.
 *
 * Conceptually the header has ‘actions’ and ‘controls’. *Actions* do things
 * based on the current view, like playing a presentation or creating a CSV
 * export. *Controls* manipulate what’s displayed in the view, like changing
 * sort order on the calendar or hiding content from moment slides.
 *
 * @see https://share.goabstract.com/d169db67-4817-4e3c-bd93-e6dd89c7bb70?sha=9a1e5b5bdec04588859b325417e26c121f95dae5
 */
export const ViewHeader: VFC<ViewHeaderProps> = ({
  actionsChildren,
  actionMenuOptions = [],
  controlsChildren = [],
  defaultTitle = '(untitled)',
  navigator,
  onDestroy,
  onNavigateToParent,
  onSearch,
  parentTitle,
  searchable = true,
  searchControlsHtmlId,
  searchInputWrapperId = '',
  searchPlaceholder,
  theme = 'light',
  title,
}) => {
  const hasActionsMenu = actionMenuOptions.length > 0
  const hasActions =
    Boolean(actionsChildren) || hasActionsMenu || Boolean(onDestroy)
  const hasControls = controlsChildren.length > 0
  const hasParent = typeof parentTitle === 'string'

  const buttonDisplay = hasParent
    ? { buttonText: parentTitle, iconPosition: ButtonIconPosition.leading }
    : { ariaLabel: 'Back' }

  return (
    <StrictMode>
      <header css={styles.container({ theme })}>
        <div css={styles.metadataLine}>
          <Conditional condition={hasParent}>
            <div css={styles.parent}>
              <Button
                {...buttonDisplay}
                variant="tertiary"
                disabled={!onNavigateToParent}
                icon={
                  <Icon
                    ariaHidden
                    glyph="gem-chevron-w"
                    height={Size.Icon}
                    width={Size.Icon}
                  />
                }
                onClick={onNavigateToParent}
                name="parent-url"
                theme={theme}
              />
            </div>
          </Conditional>

          <div css={styles.titleAndSearch({ hasParent })}>
            <h1
              css={styles.title({
                hasTitle: Boolean(title),
                theme,
              })}
            >
              {capitalCase(title || defaultTitle)}
            </h1>

            <Conditional condition={searchable}>
              <div id={searchInputWrapperId} css={styles.searchContainer}>
                <SearchInput
                  collapsible={true}
                  controlsHtmlId={searchControlsHtmlId}
                  labelText={`Search ${title || defaultTitle}`}
                  onSearch={onSearch}
                  placeholder={searchPlaceholder}
                  styleOverrides={{ inputContainer: styles.search }}
                  theme={theme}
                />
              </div>
            </Conditional>
          </div>

          <div css={styles.avatar}>
            <ConnectedCurrentUserAvatar
              displayRole={true}
              onClick={() => openDrawer('darwin')}
              size={Spacing.XS * 7}
              theme={theme}
            />
          </div>
        </div>

        <Conditional condition={hasActions || hasControls}>
          <div
            css={styles.children({
              hasActions,
              hasControls,
            })}
          >
            <Conditional condition={hasControls}>
              <div css={styles.controls}>
                {controlsChildren.map((controlSet, index) => (
                  <div css={styles.controlSet({ theme })} key={index}>
                    <ChildrenWithInjectedProps
                      styleOverrides={{
                        button: styles.control({ theme }),
                      }}
                      theme={theme}
                    >
                      {controlSet}
                    </ChildrenWithInjectedProps>
                  </div>
                ))}
              </div>
            </Conditional>

            <Conditional condition={Boolean(navigator)}>
              <ChildrenWithInjectedProps theme={theme}>
                {navigator}
              </ChildrenWithInjectedProps>
            </Conditional>

            <Conditional condition={hasActions}>
              <div css={styles.actions}>
                <Conditional condition={actionMenuOptions.length > 0}>
                  <ActionsMenu.Base
                    align="end"
                    tooltipContent="More Options"
                    zIndex={999}
                  >
                    {actionMenuOptions.map((option, index) => (
                      <DropdownMenu.Item key={index} onClick={option.onClick}>
                        {option.title}
                      </DropdownMenu.Item>
                    ))}
                    {Boolean(onDestroy) && (
                      <Fragment>
                        <ActionsMenu.Separator />
                        <ActionsMenu.Item
                          key="view-header-destroy"
                          destructive={true}
                          onKeyDown={(e) => e.stopPropagation()}
                          onSelect={onDestroy}
                        >
                          Delete
                        </ActionsMenu.Item>
                      </Fragment>
                    )}
                  </ActionsMenu.Base>
                </Conditional>

                <ChildrenWithInjectedProps
                  styleOverrides={{
                    button: styles.action,
                  }}
                  theme={theme}
                >
                  {actionsChildren}
                </ChildrenWithInjectedProps>

                <Conditional condition={Boolean(onDestroy) && !hasActionsMenu}>
                  <Button
                    buttonText="Delete"
                    variant="primaryDestructive"
                    icon={
                      <Icon
                        ariaHidden
                        height={Size.Icon}
                        glyph="gem-trash"
                        width={Size.Icon}
                      />
                    }
                    iconPosition={ButtonIconPosition.leading}
                    name="delete-button"
                    onClick={onDestroy}
                    styleOverrides={{
                      button: styles.action,
                    }}
                    theme={theme}
                  />
                </Conditional>
              </div>
            </Conditional>
          </div>
        </Conditional>
      </header>
    </StrictMode>
  )
}
ViewHeader.displayName = 'Lapidary Unstable: ViewHeader'
